import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import { CUSTOMERIO_CDN_URL, CUSTOMERIO_WRITE_KEY, IS_SA } from '../config/appConfig';
import { OpenPanelCategories, OpenPanelEvents } from '../constants/openPanelEvents';
import { Roles } from '../constants/roles';

// Mirrors openPanelHelper.js 1:1 — same guards, same buffering, same dedupe
// window, sent alongside every OpenPanel call. Fire-and-forget: every export
// is a silent no-op when REACT_APP_CUSTOMERIO_WRITE_KEY is not configured.
let cio = null;

let runtimeEnabled = false;

// Same three-state kill-switch as OpenPanel's: `systemSettings == null` means
// "not answered yet", not "disabled" — see openPanelHelper.js for the full
// rationale. Own buffer, independent of OpenPanel's.
let settingsResolved = false;
const PENDING_LIMIT = 50;
let pending = [];

const dispatch = (send) => {
	if (!settingsResolved) {
		pending.push(send);
		if (pending.length > PENDING_LIMIT) {
			pending.shift();
		}
		return;
	}
	if (!runtimeEnabled || !cio) {
		return;
	}
	try {
		send();
	} catch (error) {
		console.warn('Customer.io send failed.', error);
	}
};

// Own dedupe state, independent of OpenPanel's trackOpenPanelView.
const DEDUPE_WINDOW_MS = 2000;
let lastViewEventKey = null;
let lastViewEventAt = 0;

const getRegion = () => (IS_SA ? 'SA' : 'AE');
const getLanguage = () => (localStorage.getItem('i18nextLng') === 'ar' ? 'ar' : 'en');

const toUserType = (role) => {
	const roles = Array.isArray(role) ? role : [role];
	if (roles.includes(Roles.Investor)) {
		return 'investor';
	}
	if (roles.includes(Roles.Borrower)) {
		return 'borrower';
	}
	return null;
};

export const syncCustomerIoEnabled = (systemSettings) => {
	if (systemSettings == null) {
		return;
	}

	runtimeEnabled = systemSettings.customerIoEnabled === true;
	settingsResolved = true;

	if (runtimeEnabled) {
		initCustomerIo();
	}

	const queued = pending;
	pending = [];
	if (runtimeEnabled && cio) {
		queued.forEach((send) => dispatch(send));
	}
};

export const markCustomerIoSettingsUnavailable = () => {
	if (settingsResolved) {
		return;
	}
	settingsResolved = true;
	runtimeEnabled = false;
	pending = [];
};

const initCustomerIo = () => {
	if (!runtimeEnabled || !CUSTOMERIO_WRITE_KEY || cio) {
		return;
	}
	try {
		cio = AnalyticsBrowser.load({
			writeKey: CUSTOMERIO_WRITE_KEY,
			// The workspace's data-pipelines CDN is region-pinned (this one is EU:
			// cdp-eu.customer.io) — without this override the SDK fetches remote
			// settings from the wrong (default US) CDN.
			...(CUSTOMERIO_CDN_URL ? { cdnURL: CUSTOMERIO_CDN_URL } : {}),
		});
	} catch (error) {
		cio = null;
		console.warn('Customer.io init failed.', error);
	}
};

export const trackCustomerIo = (event, properties = {}) => {
	try {
		const payload = { region: getRegion(), ...properties };
		dispatch(() => cio.track(event, payload));
	} catch (error) {
		console.warn('Customer.io track failed.', error);
	}
};

// Mirrors trackOpenPanelScreenView: origin+pathname only, never the query
// string — some routes carry a password-reset token or an email there.
// cio.page(category, name, properties); category/name are separate positional
// args in this SDK (unlike OpenPanel's single path string), so the sanitized
// path is passed as `name` with no category.
export const trackCustomerIoScreenView = () => {
	try {
		const path = `${window.location.origin}${window.location.pathname}`;
		dispatch(() => cio.page(null, path, { region: getRegion() }));
	} catch (error) {
		console.warn('Customer.io screen view failed.', error);
	}
};

export const trackCustomerIoView = (event, properties = {}) => {
	if (settingsResolved && (!runtimeEnabled || !cio)) {
		return;
	}
	try {
		const eventKey = `${event}|${JSON.stringify(properties)}`;
		const now = Date.now();
		if (eventKey === lastViewEventKey && now - lastViewEventAt < DEDUPE_WINDOW_MS) {
			return;
		}
		lastViewEventKey = eventKey;
		lastViewEventAt = now;
	} catch (error) {
		console.warn('Customer.io view-dedupe failed.', error);
	}
	trackCustomerIo(event, properties);
};

// cio.identify(userId, traits) takes a FLAT traits object — unlike OpenPanel's
// op.identify({ profileId, email, properties: {...} }), there is no nested
// properties wrapper here.
export const identifyCustomerIo = (user, traits = {}) => {
	if (!user?.nameid) {
		return;
	}
	try {
		const userType = toUserType(user.role);
		const payload = {
			region: getRegion(),
			language: getLanguage(),
			currency: IS_SA ? 'SAR' : 'AED',
			user_id: user.nameid,
			...(user.email ? { email: user.email } : {}),
			...(userType ? { user_type: userType } : {}),
			...traits,
		};
		dispatch(() => cio.identify(user.nameid, payload));
	} catch (error) {
		console.warn('Customer.io identify failed.', error);
	}
};

export const clearCustomerIo = () => {
	pending = [];
	lastViewEventKey = null;
	lastViewEventAt = 0;
	if (!cio) {
		return;
	}
	try {
		cio.reset();
	} catch (error) {
		console.warn('Customer.io clear failed.', error);
	}
};

export const recordInvestorSignupStarted = (user, { mobileNumber } = {}) => {
	if (!user?.nameid) {
		return;
	}
	const mobileTrait = mobileNumber ? { mobile_number: mobileNumber } : {};
	identifyCustomerIo(user, { account_status: 'pending', ...mobileTrait });
	trackCustomerIo(OpenPanelEvents.signupStarted, {
		page_name: 'Sign up',
		category: OpenPanelCategories.onboarding,
		...(user.email ? { email: user.email } : {}),
		...mobileTrait,
	});
};

export const recordBorrowerSignupCompleted = (user, { mobileNumber } = {}) => {
	if (!user?.nameid) {
		return;
	}
	const mobileTrait = mobileNumber ? { mobile_number: mobileNumber } : {};
	identifyCustomerIo(user, { account_status: 'pending', ...mobileTrait });
	trackCustomerIo(OpenPanelEvents.borrowerSignupCompleted, {
		category: OpenPanelCategories.onboarding,
	});
};

export const recordNafathCompleted = (user, { personalInfo, nationalityId, countries } = {}) => {
	const alpha2 = countries?.find((c) => c.id === nationalityId)?.alpha2Code;
	identifyCustomerIo(user, {
		...(personalInfo?.firstName && { first_name: personalInfo.firstName }),
		...(personalInfo?.lastName && { last_name: personalInfo.lastName }),
		...(alpha2 && { nationality: alpha2 }),
	});
	trackCustomerIo(OpenPanelEvents.nafathCompleted, {
		category: OpenPanelCategories.kyc,
	});
};
